import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { AbstractFormControl } from '../abstract-form-control';

@Component({
  selector: 'app-tag-selector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TagModule],
  template: `
    <div class="flex gap-2">
      @for (tag of options(); track $index) {
        <p-tag
          [value]="tag.label"
          class="cursor-pointer"
          [rounded]="true"
          (click)="onInputChange(tag.value)"
          [severity]="value().includes(tag.value) ? 'primary' : 'secondary'"
          [style.--primary-color]="
            tag.color && value().includes(tag.value)
              ? tag.color.startsWith('--')
                ? 'var(' + tag.color + ')'
                : tag.color
              : null
          "
        />
      }
    </div>
  `,
  styles: [
    `
      .p-tag {
        --p-tag-primary-background: oklch(from var(--app-tag-selector-color) l c h / 0.1);
        --p-tag-primary-color: var(--color-black);

        --p-tag-padding: 4px 8px;
        --p-tag-font-weight: 400;
        --p-tag-font-size: 12px;
        --p-tag-rounded-border-radius: 24px;

        border: 1px solid var(--app-tag-selector-color);
      }

      .p-tag-primary {
        --app-tag-selector-color: var(--primary-color);
      }

      .p-tag-secondary {
        --app-tag-selector-color: var(--color-gray-300);
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TagSelectorComponent),
      multi: true,
    },
  ],
})
export class TagSelectorComponent extends AbstractFormControl<string[]> implements ControlValueAccessor, Validator {
  override innerCtrl = undefined;

  options = input.required<{ label: string; value: string; color?: string }[]>();
  value = signal<string[]>([]);

  onInputChange(value: string): void {
    if (!value) {
      return;
    }
    if (this.value().includes(value)) {
      this.value.update((values) => values.filter((sel) => sel !== value));
    } else {
      this.value.update((values) => [...values, value]);
    }
    this.onChange(this.value());
    this.onTouch();
    this.onValidate();
  }

  writeValue(value: string[]): void {
    this.value.set(value);
  }
}
